import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { mergeEmbedThemes } from '../../../../packages/survey-core/src/embed-theme';
import { createEmbedLifecycleEvent, embedHostCommandSchema } from '../../../../packages/survey-core/src/embed-protocol';
import type { EmbedLifecycleEvent, EmbedProtocolContext } from '@responsedock/types';
import { useAuthStore } from '../store/auth';
import { updateStoredEmbedTheme } from '../lib/embedSession';
import { allowedEmbedNavigation } from '../lib/embedNavigation';
import { observeEmbeddedContentSize } from '../lib/embedResize';

export function EmbeddedThemeBridge() {
  const location = useLocation();
  const navigate = useNavigate();
  const isEmbedded = useAuthStore((state) => state.isEmbedded);
  const embedContext = useAuthStore((state) => state.embedContext);
  const setEmbedTheme = useAuthStore((state) => state.setEmbedTheme);
  // The handshake must run once per embedded session. Theme updates replace
  // embedContext and navigation replaces navigate; reading them through refs
  // keeps those changes from re-announcing readiness, which hosts answer with
  // another theme update.
  const embedContextRef = React.useRef(embedContext);
  embedContextRef.current = embedContext;
  const navigateRef = React.useRef(navigate);
  navigateRef.current = navigate;
  const allowedOriginSetting = embedContext?.allowedOrigin;
  const embeddedRole = embedContext?.role;
  const workspaceId = embedContext?.workspaceId;
  const surveyId = embedContext?.surveyId ?? null;

  React.useEffect(() => {
    if (!isEmbedded || !allowedOriginSetting || !embeddedRole || !workspaceId || window.parent === window) return;
    const allowedOrigin = new URL(allowedOriginSetting).origin;
    const role = embeddedRole;
    const context: EmbedProtocolContext = {
      frame: embeddedRole === 'respondent' ? 'respondent-dashboard' : 'admin',
      workspaceId,
      surveyId,
    };
    const post = <T extends EmbedLifecycleEvent['type']>(
      type: T,
      payload: Extract<EmbedLifecycleEvent, { type: T }>['payload'],
    ) => window.parent.postMessage(createEmbedLifecycleEvent(type as never, payload as never, context), allowedOrigin);

    function onMessage(event: MessageEvent) {
      if (event.source !== window.parent || event.origin !== allowedOrigin) return;
      if (event.data?.type === 'responsedock.theme.update') {
        try {
          const theme = mergeEmbedThemes(embedContextRef.current?.theme, event.data.theme);
          setEmbedTheme(theme);
          updateStoredEmbedTheme(theme);
          window.parent.postMessage({ type: 'responsedock.theme.updated' }, allowedOrigin);
        } catch {
          window.parent.postMessage({ type: 'responsedock.theme.rejected' }, allowedOrigin);
        }
        return;
      }

      const parsed = embedHostCommandSchema.safeParse(event.data);
      if (!parsed.success) return;
      const command = parsed.data;
      try {
        if (command.type === 'responsedock.command.ping') {
          post('responsedock.lifecycle.pong', { requestId: command.requestId });
        } else if (command.type === 'responsedock.command.theme.update') {
          const theme = mergeEmbedThemes(embedContextRef.current?.theme, command.theme);
          setEmbedTheme(theme);
          updateStoredEmbedTheme(theme);
          post('responsedock.lifecycle.theme_updated', {});
        } else if (command.type === 'responsedock.command.locale.update') {
          window.dispatchEvent(new CustomEvent('responsedock:locale-update', { detail: command.locale }));
          post('responsedock.lifecycle.locale_updated', { locale: command.locale });
        } else if (command.type === 'responsedock.command.navigate') {
          if (!allowedEmbedNavigation(command.path, role, embedContextRef.current?.capabilities)) throw new Error('Route is outside the embedded surface.');
          navigateRef.current(command.path);
        } else if (command.type === 'responsedock.command.close') {
          post('responsedock.lifecycle.close_requested', { reason: command.reason ?? 'host-command' });
        }
      } catch (error) {
        post('responsedock.lifecycle.error', {
          code: 'COMMAND_REJECTED',
          message: error instanceof Error ? error.message : 'The host command was rejected.',
          recoverable: true,
        });
      }
    }

    function onSessionExpired(event: Event) {
      const reason = event instanceof CustomEvent && typeof event.detail === 'string' ? event.detail : 'The embedded bearer session expired.';
      post('responsedock.lifecycle.session_expired', { reason });
    }

    function onWindowError(event: ErrorEvent) {
      post('responsedock.lifecycle.error', { code: 'UNHANDLED_ERROR', message: event.message || 'An unexpected embedded error occurred.', recoverable: false });
    }

    const stopObservingSize = observeEmbeddedContentSize((size) => post('responsedock.lifecycle.resize', size));

    window.addEventListener('message', onMessage);
    window.addEventListener('responsedock:session-expired', onSessionExpired);
    window.addEventListener('error', onWindowError);
    window.parent.postMessage({ type: 'responsedock.embed.ready' }, allowedOrigin);
    post('responsedock.lifecycle.ready', { capabilities: ['theme', 'resize', 'navigation', 'locale', 'session', 'completion'] });
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('responsedock:session-expired', onSessionExpired);
      window.removeEventListener('error', onWindowError);
      stopObservingSize();
    };
  }, [allowedOriginSetting, embeddedRole, isEmbedded, setEmbedTheme, surveyId, workspaceId]);

  React.useEffect(() => {
    if (!isEmbedded || !allowedOriginSetting || !embeddedRole || !workspaceId || window.parent === window) return;
    window.parent.postMessage(createEmbedLifecycleEvent(
      'responsedock.lifecycle.navigation',
      { path: `${location.pathname}${location.search}` },
      {
        frame: embeddedRole === 'respondent' ? 'respondent-dashboard' : 'admin',
        workspaceId,
        surveyId,
      },
    ), new URL(allowedOriginSetting).origin);
  }, [allowedOriginSetting, embeddedRole, isEmbedded, location.pathname, location.search, surveyId, workspaceId]);

  return null;
}
